import React, { useState, useMemo, createContext, useEffect, useRef } from "react";
import {
    BrowserRouter,
    Switch,
    Route,
    Redirect,
} from "react-router-dom";

import PageVisibility from 'react-page-visibility/dist/umd/PageVisibility'
import { isPageVisible } from './utils/isVisible'
import UserRegister from "./pages/UserRegister";
import Trial from "./pages/Trial";
import Login from "./pages/Login";
import Plans from "./pages/Plans";
import Products from "./pages/Products";
import AdminDashboard from "./pages/AdminDashboard";
import ScoutObservability from "./pages/ScoutObservability";
import ServiceSettings from "./pages/ServiceSettings";
import Dashboard from "./pages/Dash";
import Chat from "./pages/Chat";
import Report from "./pages/Report";
import CreatePassword from "./pages/CreatePassword";
import ResetPassword from "./pages/ResetPassword";
import ScanScoutQR from "./pages/ScanScoutQR";
import Settings from "./pages/Settings";
import ManageCalls from "./pages/ManageCalls";
import Clients from "./pages/Clients";
import Metrics from "./components/Metrics";
import Onboarding from "./pages/Onboarding";
import SupportEnterprise from "./pages/SupportEnterprise";
import Crm from "./pages/Crm";
import AdminLogin from "./pages/AdminLogin";
import MainPage from "./pages/Main"
import EntraLogin from "./pages/EntraLogin"
import Portal from "./pages/Portal"

// import SocketQueueComponent from "./SocketQueueComponent";
import UploadLoaderComponent from "./components/UploadLoaderComponent";
import { Box, Button } from "@mui/material";
import IdentificateAgentDialog from "./components/Dialogs/IdentificateAgentDialog";
import InstallKwik from "./utils/InstallKwik";
import isMobile, {lowResolution} from "./utils/isMobile";

import InternalChatMenu from "./components/Menu/InternalChatMenu";
import SupportMenu from "./components/Menu/SupportMenu";
import NotificationsMenu from "./components/Menu/NotificationsMenu";
import WebphoneMenu from "./components/Menu/WebphoneMenu";
import SocketQueueComponent from "./SocketQueueComponent";

import ConfirmationDialog from "./components/Dialogs/Confirmation"

import { TelephonyProvider }from "./contexts/TelephonyContext";
import { MenuProvider }from "./contexts/MenuContext";

import CortexLogo from './assets/images/cortex-logo-vertical.png'

import IconButton from '@mui/material/IconButton';

import { isAuthenticated, isSuperAdminAuthenticated, getSession } from "./auth";

import userPermissions from './components/User/Permissions';

import useNotification from "./components/Notification";
import KwikAssistant from "./components/KwikAssistant";
import BuilderPage from "./pages/Builder";
import ChannelsPage from "./pages/Channels";
import CortexPage from "./pages/Cortex"
import AutomationPage from "./pages/Automation";
import ScoutPage from "./pages/Scout"
import MyTeamPage from "./pages/MyTeam"
import NFSe from "./pages/Nfse";
import Enterprises from "./pages/Enterprises";
import Managers from "./pages/Managers";
import Payments from "./pages/Payments";
import Sequences from './pages/Sequences'
import NationalNFSeSettings from './pages/NationalNFSeSettings'
import FBTrial from "./fbtrial";
import WBTrial from "./wbtrial";
import EmailTemplates from "./pages/EmailTemplates";
import Boletos from "./pages/Boletos";
import { ReactNotifications } from "react-notifications-component";
import { setFaviconBadge } from "./utils/favicon";

import api from "./api"

const groups = userPermissions();

var ext_visible = false

export const QueueSocket = createContext()

export const CallSocket = createContext();

const checkPermission = (permission) => {
    if (! permission)
        return true
    if (permission === "admin" && groups.isAdmin)
        return true
    if (permission === "supervisor" && groups.isSupervisor)
        return true
    if (permission === "create_template" && (getSession().profile.can_create_template || groups.isAdmin || groups.isSupervisor))
        return true
}

const AdminPrivateRoute = ({ component: Component, ...rest }) => {
    if (! isSuperAdminAuthenticated)
        return (
            <Route
                exact {...rest}
                render={props => (<Redirect to={{
                    pathname: '/superadmin',
                    state: { from: props.location }
                }} />)}
            />
        )

    return (
        <Route exact {...rest}
            render={props => (<Component {...props} />)}
        />
    );
}

const PrivateRoute = ({ permission: permission, component: Component, ...rest }) => {

    const hasPermission = checkPermission(permission);

    const platforms = getSession()?.profile.enterprise.platforms;
    const channels = getSession()?.profile.enterprise.channels;

    // DEPRECATED
    // const isEnterpriseSetup = !platforms?.whatsapp || platforms?.whatsapp && channels?.length !== 0;
    
    const isEnterpriseSetup = true

    if (!isAuthenticated) {
        return (
            <Route
                exact {...rest}
                render={props => (<Redirect to={{
                    pathname: '/',
                    state: { from: props.location }
                }} />)}
            />
        );
    }

    if (isAuthenticated && !isEnterpriseSetup && rest.path != '/onboarding') {
        return (
            <Route
                exact {...rest}
                render={props => (<Redirect to={{
                    pathname: '/onboarding',
                    state: { from: props.location }
                }} />)}
            />
        );
    }

    if (isAuthenticated && !hasPermission ||
        isAuthenticated && isEnterpriseSetup && rest.path == '/onboarding') {
        return (
            <Route
                exact {...rest}
                render={props => (<Redirect to={{
                    pathname: '/chat',
                    state: { from: props.location }
                }} />)}
            />
        );
    }

    return (
        <Route exact {...rest}
            render={props => (<Component {...props} />)}
        />
    );
}

const ChangeNotificationTexts = (type, numberChannels, unreadMessages) => {
    let channelPlural = '';
    let messagePlural = {
        title: '',
        body: ''
    };

    if (type === 'newMessage') {
        if (unreadMessages > 1) {
            messagePlural.title = 'novas mensagens';
            messagePlural.body = 'Novas mensagens';

        } else {
            messagePlural.title = 'nova mensagem';
            messagePlural.body = 'Nova mensagem';
        }

        if (numberChannels > 1)
            channelPlural = 'conversações';
        else
            channelPlural = 'conversação';

    } else if (type === 'newChat') {
        if (numberChannels > 1) {
            messagePlural.title = 'novos Chats';
            messagePlural.body = 'Novos Chats';

        } else {
            messagePlural.title = 'novo Chat';
            messagePlural.body = 'Novo Chat';
        }
    }

    return {
        channelPlural: channelPlural,
        messagePlural: messagePlural
    };
}

let _messageSilenced = false
const resetMessageSilence = () => { _messageSilenced = false }

const browserNotification = async (type, numberChannels, unreadMessages, extraText='') => {

    const settings = getSession()?.profile?.enterprise?.settings || {}
    const defaultNotificationSettings = {
        new_message: { notification: true, sound: true, silence_after_first: false, visibility: 'always' },
        new_chat: { notification: true, sound: true, visibility: 'always' },
        new_queue: { notification: true, sound: true, visibility: 'always' }
    }
    const notificationSettings = settings.notification_settings || defaultNotificationSettings

    const isNewMessage = type === 'newMessage'
    const isNewChat = type === 'newChat'

    const notificationConfig = isNewMessage
        ? (notificationSettings.new_message || defaultNotificationSettings.new_message)
        : isNewChat
            ? extraText?.includes('em fila')
                ? (notificationSettings.new_queue || defaultNotificationSettings.new_queue)
                : (notificationSettings.new_chat || defaultNotificationSettings.new_chat)
            : { notification: true, sound: true, visibility: 'always' }

    const notificationEnabled = notificationConfig.notification !== false
    const soundEnabled = notificationConfig.sound !== false
    const visibility = notificationConfig.visibility || 'always'

    if (isNewMessage && notificationConfig.silence_after_first && _messageSilenced)
        return

    const shouldShowByVisibility = () => {
        if (visibility === 'always')
            return true

        const visible = isPageVisible()

        if (visibility === 'other_tabs')
            return visible
        if (visibility === 'outside_kwik')
            return !visible

        return !visible
    }

    if (!notificationEnabled && !soundEnabled)
        return

    let title = ''
    let body = ''

    if (type === 'newMessage') {
        const descriptions = ChangeNotificationTexts(type, numberChannels, unreadMessages)
        title = `${unreadMessages} ${descriptions.messagePlural.title}`
        body = `${descriptions.messagePlural.body} ${extraText}.`

    } else if (type === 'newChat') {
        const descriptions = ChangeNotificationTexts(type, numberChannels)
        title = `${numberChannels} ${descriptions.messagePlural.title}`
        body = extraText
            ? `${descriptions.messagePlural.body} ${extraText}.`
            : `${descriptions.messagePlural.body} aguardando na fila.`

    } else if (type === 'newCall') {
        if (isPageVisible())
            return
        title = `Nova chamada de voz`
        body = `Você recebebeu uma nova ligação.`
    }

    if (!shouldShowByVisibility()) {
        return
    }

    let audio
    if (soundEnabled) {
        audio = new Audio(`${window.location.origin}/new_message.mp3`)
        const notificationAudio = getSession()?.profile?.enterprise?.audio_notification
        if (notificationAudio) {
            audio = new Audio(`${window.location.origin}/export/${notificationAudio.split('/export/')[1]}`)
        }
        try {
            await audio.play()
        } catch (e) {
            console.error(e)
        }
    }

    if (!notificationEnabled)
        return

    if (!('Notification' in window)) {
        useNotification(
            'Atenção!',
            'Este navegador não suporta notificação na área de trabalho.',
            'warning'
        )
        return
    }

    const options = {
        body: body,
        lang: 'pt-BR',
        icon: `${window.location.origin}/kwik_favicon.png`,
        silent: true,
    }

    if (isNewMessage && notificationConfig.silence_after_first)
        _messageSilenced = true

    const registrations = await navigator.serviceWorker.getRegistrations()
    if (registrations.length > 0) {
        registrations[0].showNotification(title, options)
    }
}


const deniedNotification = (permission) => {
    if (permission === "denied") {
        useNotification(
            'Permissão negada!',
            'Você não permitiu que o Kwik envie notificações de novas mensagens para você.',
            'danger'
        );
    }
}

const isSupported = () =>
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window

const requestPermission = () => {
    if (!isSupported()) return
    return new Promise((resolve, reject) => {
        Notification.requestPermission().then(permission => {
            // If the user accepts, let's create a notification
            if (permission === "granted") {
                resolve();
            } else {
                reject(permission);
            }
        });
    });
}

// Detects if device is on iOS 
const isIos = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
}

const sendNotification = (type, numberChannels, unreadMessages, extraText='') => {

    if (isIos()) {
        return;
    }

    if (type === 'newMessage' || type === 'newChat') {
        setFaviconBadge('message', true);
    } else if (type === 'newCall') {
        setFaviconBadge('call', true);
    }

    // Let's check if the browser supports notifications
    if (!("Notification" in window)) {
        useNotification(
            'Atenção!',
            'Este navegador não suporta notificação na área de trabalho.',
            'warning'
        );
    }

    // Let's check whether notification permissions have already been granted
    else if (Notification.permission === "granted") {
        // Open new message notification
        try {
            browserNotification(type, numberChannels, unreadMessages, extraText);
        }
        catch (e) {
            console.error(e)
                ;
        }

    }

    // Otherwise, we need to ask the user for permission
    else if (Notification.permission !== "denied") {
        requestPermission()
            .then(() => {
                // Open new message notification
                try {
                    browserNotification(type, numberChannels, unreadMessages, extraText);
                }
                catch (e) {
                    console.error(e)
                        ;
                }
            })
            .catch(permission => {
                deniedNotification(permission);
            });
    }

    // At last, if the user has denied notifications, and you
    // want to be respectful there is no need to bother them any more.
}

const AssistantDOM = () => {
    return (
        <>
            {(isAuthenticated) && <KwikAssistant key='assistant-main-key-dont-remove-fromdom'/>}
        </>
    )
}

const Routes = () => {

    const cortexRef = useRef()

    const webphoneBacklistedPaths = [
        '', // login page
        'metrics',
        'register',
        'createPassword',
        'resetPassword',
        'plans',
        'serviceSettings',
        'onboarding',
        'assistant',
        'trial',
        'fbtrial',
        'wbtrial',
        'plans', 
        'products', 
        'enterprises', 
        'managers', 
        'nfse', 
        'paymenthistory', 
        'sequences', 
        'templateemail',
        'superadmin',
        'superadmin-dashboard',
        'scout-observability',
        "cortex",
        'login',
        "support",
    ];

    const shouldRenderWidgets = () => {

        const url = window.location.href;
        const parts = url.split('/');
        const pathOne = parts[parts.length - 1];
        const path = pathOne.split('?')[0]
        const firstSegment = (parts[3] || '').split('?')[0]

        return !webphoneBacklistedPaths.includes(path) && !webphoneBacklistedPaths.includes(firstSegment);
    }

    //Calling this hook will cause state to update, you can use it to refresh any internal component
    //with a useEffect

    const [chatState, updateChatState] = useState()
    const [notifications, setNotifications] = useState(false)
    const [connections, updateConnections] = useState([])
    const [visible, setVisible] = useState(true)
    const [refresh, setRefresh] = useState(false)
    const [showAssistant, setShowAssistant] = useState(false)
    const [cache, setCache] = useState()
    const [failedContacts, setFailedContacts] = useState([])
    const [width, setWidth] = useState(() => {
        // Retrieve the width from localStorage or set default value
        const savedWidth = localStorage.getItem('assistantWidth');
        return savedWidth ? parseFloat(savedWidth) : 0.25;
    });

    // Save the width to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('assistantWidth', width);
    }, [width]);

    const reloadState = () => { setRefresh(p => !p) }

    const user = getSession()

    const agentIdentification = user?.profile.enterprise.settings.agent_identification
    const isAgentIdentificated = user?.profile.enterprise.settings.isAgentIdentificated

    useEffect(() => {
        ext_visible = visible
    }, [visible, notifications])

    useEffect(async () => {
        requestPermission()

        const registrations = await navigator.serviceWorker.getRegistrations()

        if (registrations && registrations.length > 0)
            registrations.map(async each => { await each.unregister() })

        await navigator.serviceWorker.register('/sw.js');

        window.onresize = reloadState
    }, [])

    useEffect(() => {
        if (location.pathname == '/chat' || location.pathname == '/manageCalls' || !chatState)
            return
        const message = chatState.message
        switch (message) {
            case 'NEW_CHAT':
                sendNotification('newChat', 1, 1, 'em atendimento');
                break;
            default:
                break;
        }

    }, [chatState])

    const route = useMemo(() => {

        return (
            <BrowserRouter>
                <TelephonyProvider>
                    <MenuProvider>
                        {(shouldRenderWidgets() && !isMobile()) &&
                            <Box id="widgets-container" className="widgets-container">
                                <WebphoneMenu />
                                <InternalChatMenu />
                                <SupportMenu />
                                <NotificationsMenu />
                            </Box>
                        }
                        <Switch >
                            <Route exact path='/metrics' component={Metrics} />
                            <Route exact path='/register' component={UserRegister} />
                            <Route exact path='/trial' component={Trial} />
                            <Route exact path='/' component={Login} />
                            <Route exact path='/fbtrial' component={FBTrial} />
                            <Route exact path='/wbtrial' component={WBTrial} />
                            <Route exact path='/superadmin' component={AdminLogin} />
                            <Route path='/login/:vanityUrl' component={EntraLogin} />
                            <Route path='/createPassword' component={CreatePassword} />
                            <Route path='/resetPassword' component={ResetPassword} />
                            <Route path='/scanScoutQR' component={ScanScoutQR} />
                            <AdminPrivateRoute path='/superadmin-dashboard' component={AdminDashboard} />
                            <AdminPrivateRoute path='/scout-observability' component={ScoutObservability} />
                            <AdminPrivateRoute path='/products' component={Products} />
                            <AdminPrivateRoute path='/plans' component={Plans} />
                            <AdminPrivateRoute path='/nfse' component={NFSe} />
                            <AdminPrivateRoute path='/boletos' component={Boletos} />
                            <AdminPrivateRoute path='/paymenthistory' component={Payments} />
                            <AdminPrivateRoute path='/sequences' component={Sequences} />
                            <AdminPrivateRoute path='/national-nfse-settings' component={NationalNFSeSettings} />
                            <AdminPrivateRoute path='/managers' component={Managers} />
                            <AdminPrivateRoute path='/enterprises' component={Enterprises} />
                            <AdminPrivateRoute path='/templateemail' component={EmailTemplates} />
                            <PrivateRoute path='/serviceSettings' component={ServiceSettings} />
                            <PrivateRoute path='/dashboard' component={Dashboard} permission="supervisor" />
                            <PrivateRoute path='/chat' component={MainPage} />
                            <PrivateRoute path='/report' component={Report} permission="supervisor" />
                            <PrivateRoute path='/settings' component={Settings} permission="supervisor" />
                            <PrivateRoute path='/automation' component={AutomationPage} permission="admin" />
                            <PrivateRoute path='/scout' component={ScoutPage} permission="supervisor" />
                            {/*<PrivateRoute path='/myteam' component={MyTeamPage} permission="supervisor" />*/}
                            <PrivateRoute path='/builder' component={BuilderPage} permission="admin" />
                            <PrivateRoute path='/channels' component={ChannelsPage} permission="admin" />
    						<PrivateRoute path="/cortex" component={CortexPage} permission="admin" />
                            {/*<PrivateRoute path='/manageCalls' component={ManageCalls} permission="supervisor" />*/}
                            {/*<PrivateRoute path='/crm' component={Crm} />*/}
                            <PrivateRoute path='/onboarding' component={Onboarding} />
                            <PrivateRoute path='/support/enterprise' component={SupportEnterprise} />
                            <PrivateRoute path='/portal' component={Portal} />
                            {/*<PrivateRoute path='/assistant' component={KwikAssistant} />*/}
                        </Switch>
                    </MenuProvider>
                </TelephonyProvider>
            </BrowserRouter>
        )
    }, [visible])
    

    const startResizing = (e) => {
        document.addEventListener("mousemove", resize);
        document.addEventListener("mouseup", stopResizing);
    };

    const resize = (e) => {
            if (e.movementX > 0) {
                setWidth(prev => {
                    const newWidth = prev - 0.005;
                    localStorage.setItem('assistantWidth', newWidth);
                    return newWidth;
                });
            } else {
                setWidth(prev => {
                    const newWidth = prev + 0.005;
                    localStorage.setItem('assistantWidth', newWidth);
                    return newWidth;
                });
            }
    };

    const stopResizing = () => {
        document.removeEventListener("mousemove", resize);
        document.removeEventListener("mouseup", stopResizing);
    };

    return (
        <>
            <PageVisibility onChange={(event) => { setVisible(event); setNotifications(event); if (event) resetMessageSilence() }}></PageVisibility>
            <ReactNotifications />
            {!isIos() && <InstallKwik />}
            <QueueSocket.Provider value={{
                chatState, updateChatState, notifications,
                setNotifications, updateConnections, connections,
                sendNotification, visible, cache, setCache, setShowAssistant,
                cortexRef, failedContacts, setFailedContacts
            }}>
                <Box className='kwik-mainWrapper'>
                    <Box className='kwik-mainRoute' >
                        {route}
                    </Box>
                    <Box style={{display: 'none'}} ref={cortexRef}>{(isAuthenticated) && <KwikAssistant />}</Box>
                    {/*{(isAuthenticated && shouldRenderWidgets() && !(lowResolution())) &&
                        <Box sx={{ 
                            flex: showAssistant ? width : .02, 
                            maxWidth:'50%',
                            backgroundColor: "white", 
                            position:'relative',
                            zIndex:'2000',
                            display: "flex", 
                            flexDirection: 'row', 
                            alignItems: "center", 
                            justifyContent: "center", 
                            borderLeft: "1px solid rgba(0, 0, 0, 0.12)" 
                        }}
                        onMouseDown={startResizing}
                        >
                            {! showAssistant &&
                                <IconButton className="kwik-cortex-open-btn" onClick={() => setShowAssistant(p => !p)}>
                                    <img src={CortexLogo} className="kwik-cortex-logo"/>
                                </IconButton>
                            }
                            <span
                                style={{ display: showAssistant ? 'block' : 'none', height: "100%", width: '100%' }}>
                                {AssistantDOM()}
                            </span>
                        </Box>
                    }*/}
                </Box>
                {agentIdentification && !isAgentIdentificated ? <IdentificateAgentDialog refresh={reloadState} /> : null}
                <UploadLoaderComponent />
                <SocketQueueComponent />
            </QueueSocket.Provider>
        </>
    );
}

export default Routes;
